import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Trash2, ArrowLeft, Save, Search, Plus, Calculator, CheckCircle2, AlertTriangle, Layers, Percent } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const ParametreChambre = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const company_id = user?.company_id || user?.companyId;

    const { chambreId, chambreNumero } = location.state || {};
    const effectiveChambreId = chambreId || id;

    const [lignes, setLignes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [selectedPrestation, setSelectedPrestation] = useState(null);
    const [quantiteSaisie, setQuantiteSaisie] = useState(1);
    const [montantSaisie, setMontantSaisie] = useState(""); 
    
    const [prestationsDisponibles, setPrestationsDisponibles] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({ regime_tva_recuperable: 1, taux_tva: 18.0 });

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', 
        message: ''
    });

    const PRIMARY_COLOR = '#2563eb';
    const COLORS = {
        primary: PRIMARY_COLOR,
        lightBg: '#f8fafc',
        anthracite: '#1e293b',
        success: '#059669',
        danger: '#dc2626',
        border: '#e2e8f0'
    };

    useEffect(() => {
        if (notification.isOpen) {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message });
    };

    useEffect(() => {
        fetchData();
    }, [effectiveChambreId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [composantsRes, chambreRes, compRes] = await Promise.all([
                API.get('/hebergement/composants').catch(() => ({ data: [] })),
                effectiveChambreId ? API.get(`/hebergement/chambres/${effectiveChambreId}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
                company_id ? API.get(`/company/${company_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
            ]);

            if (compRes.data?.success && compRes.data.data) {
                setCompanyInfo({
                    regime_tva_recuperable: compRes.data.data.regime_tva_recuperable ?? 1,
                    taux_tva: compRes.data.data.taux_tva ?? 18.0
                });
            }

            const listeComposants = (composantsRes.data || []).map(c => ({
                id: c.id,
                nom: c.libelle || c.nom || c.designation || 'Sans nom',
                prix_unitaire: Number(c.prix_defaut || c.prix_unitaire || c.cmp || 0),
                soumis_tva: c.soumis_tva === 1 || c.soumis_tva === true,
                taux_tva: Number(c.taux_tva || 0)
            }));

            setPrestationsDisponibles(listeComposants);

            const data = chambreRes.data;
            if (data && data.items && Array.isArray(data.items)) {
                const mappedLignes = data.items.map(l => {
                    const compId = l.prestation_id || l.composant_id || l.id;
                    const fallbackInfo = listeComposants.find(comp => comp.id === compId);
                    return {
                        id: `LIG-${Math.random().toString(36).substr(2, 9)}`,
                        prestation_id: compId,
                        nom: l.designation || l.nom || (fallbackInfo ? fallbackInfo.nom : 'Prestation inconnue'),
                        prix_unitaire: Number(l.prix_unitaire) || (fallbackInfo ? fallbackInfo.prix_unitaire : 0),
                        quantite: Number(l.quantite || l.quantite_requise || 1),
                        soumis_tva: fallbackInfo ? fallbackInfo.soumis_tva : false,
                        taux_tva: fallbackInfo ? fallbackInfo.taux_tva : 0
                    };
                });
                setLignes(mappedLignes);
            }
        } catch (err) {
            console.error("Erreur chargement :", err);
            showToast("Impossible de charger les données", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleQtyChange = (val) => {
        setQuantiteSaisie(val);
        const qte = parseFloat(val);
        if (selectedPrestation) {
            if (!isNaN(qte) && qte > 0) {
                setMontantSaisie(Math.round(qte * selectedPrestation.prix_unitaire));
            } else {
                setMontantSaisie("");
            }
        }
    };

    const handleAmountChange = (val) => {
        setMontantSaisie(val);
        const montant = parseFloat(val);
        if (selectedPrestation && selectedPrestation.prix_unitaire > 0) {
            if (!isNaN(montant) && montant > 0) {
                const calculQte = montant / selectedPrestation.prix_unitaire;
                setQuantiteSaisie(Number(calculQte.toFixed(2)));
            } else {
                setQuantiteSaisie("");
            }
        }
    };

    useEffect(() => {
        if (selectedPrestation) {
            const qte = parseFloat(quantiteSaisie) || 0;
            const calc = Math.round(qte * selectedPrestation.prix_unitaire);
            setMontantSaisie(calc > 0 ? calc : "");
        }
    }, [selectedPrestation]);

    const confirmAddLine = () => {
        if (!selectedPrestation) return showToast("Veuillez sélectionner une prestation", "error");
        const qteNum = parseFloat(quantiteSaisie);
        const montantNum = parseFloat(montantSaisie);

        if (isNaN(qteNum) || qteNum <= 0) return showToast("La quantité doit être supérieure à 0", "error");
        if (isNaN(montantNum) || montantNum <= 0) return showToast("Le montant doit être supérieur à 0", "error");
        if (lignes.find(l => l.prestation_id === selectedPrestation.id)) return showToast("Cette prestation est déjà incluse dans cette chambre", "error");

        const newLine = {
            id: `LIG-${Date.now()}`,
            prestation_id: selectedPrestation.id,
            nom: selectedPrestation.nom,
            prix_unitaire: montantNum / qteNum,
            quantite: qteNum,
            soumis_tva: selectedPrestation.soumis_tva,
            taux_tva: selectedPrestation.taux_tva
        };
        
        setLignes([...lignes, newLine]);
        setSelectedPrestation(null);
        setQuantiteSaisie(1);
        setMontantSaisie("");
    };

    // Calculs globaux HT, TVA et TTC
    const isEntrepriseSousTva = companyInfo.regime_tva_recuperable === 1;

    const totaux = lignes.reduce((acc, ligne) => {
        const montantLigneTTC = Number(ligne.prix_unitaire) * Number(ligne.quantite);
        if (isEntrepriseSousTva && ligne.soumis_tva && ligne.taux_tva > 0) {
            const taux = ligne.taux_tva;
            const montantHT = montantLigneTTC / (1 + (taux / 100));
            const montantTVA = montantLigneTTC - montantHT;
            acc.ht += montantHT;
            acc.tva += montantTVA;
        } else {
            acc.ht += montantLigneTTC;
        }
        acc.ttc += montantLigneTTC;
        return acc;
    }, { ht: 0, tva: 0, ttc: 0 });

    const handleSave = async () => {
        if (lignes.length === 0) return showToast("Ajoutez au moins un élément tarifaire", "error");

        try {
            const itemsFormates = lignes.map(l => ({
                prestation_id: l.prestation_id,
                designation: l.nom,
                quantite: l.quantite,
                prix_unitaire: l.prix_unitaire,
                soumis_tva: l.soumis_tva ? 1 : 0,
                taux_tva: l.taux_tva
            }));

            const payload = { 
                chambre_id: effectiveChambreId, 
                items: itemsFormates, 
                tarif_total: Math.round(totaux.ttc),
                montant_ht: Math.round(totaux.ht),
                montant_tva: Math.round(totaux.tva)
            };
            await API.post('/hebergement/chambres/save-config', payload);
            
            showToast("Configuration enregistrée avec succès", "success");
            setTimeout(() => navigate('/admin/hebergement/chambres'), 1200);
        } catch (err) { 
            showToast(err.response?.data?.error || "Échec de l'enregistrement", "error"); 
        }
    };

    if (loading) return <div style={{padding: '50px', textAlign: 'center', fontWeight: '800', color: COLORS.primary, fontSize: '14px', background: '#f8fafc', height: '100vh'}}>CHARGEMENT DES PARAMÈTRES...</div>;

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' }}>
            <Sidebar />
            <main style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.1fr 1fr', position: 'relative', overflow: 'hidden' }}>
                
                {notification.isOpen && (
                    <div style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '8px', background: notification.type === 'success' ? '#065f46' : '#991b1b', color: 'white', padding: '12px 24px', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontWeight: '800', fontSize: '13px' }}>
                        {notification.type === 'success' ? <CheckCircle2 size={18}/> : <AlertTriangle size={18}/>}
                        <span>{notification.message}</span>
                    </div>
                )}

                {/* COLONNE GAUCHE : CATALOGUE */}
                <section style={{ padding: '24px', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${COLORS.border}`, background: 'white', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                        <button onClick={() => navigate(-1)} style={{ background: '#f1f5f9', border: 'none', padding: '10px', borderRadius: '10px', cursor: 'pointer', color: '#475569' }}><ArrowLeft size={18}/></button>
                        <div>
                            <h1 style={{ fontSize: '15px', fontWeight: '900', margin: 0, color: COLORS.anthracite }}>CATALOGUE DES PRESTATIONS</h1>
                            <p style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', margin: '2px 0 0 0' }}>Configuration de la Chambre N° : <span style={{ color: COLORS.primary }}>{chambreNumero || effectiveChambreId}</span></p>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '16px' }}>
                        <Search style={{ position: 'absolute', left: '14px', top: '13px', color: '#94a3b8' }} size={18} />
                        <input 
                            style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, fontWeight: '700', outline: 'none', fontSize: '13px', background: '#f8fafc', boxSizing: 'border-box' }} 
                            placeholder="Rechercher une prestation..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: 'white' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f1f5f9', color: '#475569', position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '800' }}>DÉSIGNATION</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', fontWeight: '800' }}>PRIX DÉFAUT</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '11px', width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {prestationsDisponibles.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>
                                            Aucun composant enregistré.
                                        </td>
                                    </tr>
                                ) : (
                                    prestationsDisponibles
                                        .filter(i => (i.nom || '').toLowerCase().includes(searchTerm.toLowerCase()))
                                        .map(item => {
                                            const isSelected = selectedPrestation?.id === item.id;
                                            return (
                                                <tr key={item.id} onClick={() => setSelectedPrestation(item)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSelected ? '#eff6ff' : 'transparent', transition: 'background 0.2s' }}>
                                                    <td style={{ padding: '14px 16px' }}>
                                                        <div style={{ fontWeight: '800', fontSize: '13px', color: isSelected ? COLORS.primary : COLORS.anthracite }}>{item.nom}</div>
                                                        {item.soumis_tva && isEntrepriseSousTva && (
                                                            <span style={{ fontSize: '10px', background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontWeight: '800' }}>
                                                                TVA {item.taux_tva}%
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '900', color: COLORS.success, fontSize: '13px' }}>
                                                        {Math.round(item.prix_unitaire).toLocaleString()} F
                                                    </td>
                                                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                                        <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: isSelected ? COLORS.primary : '#f1f5f9', color: isSelected ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                                            <Plus size={14} strokeWidth={3} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* COLONNE DROITE : PANIER DE CONFIGURATION & SYNTHÈSE HT / TVA / TTC */}
                <section style={{ display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
                    
                    {/* EN-TÊTE SYNTHÈSE FINANCIÈRE */}
                    <div style={{ background: COLORS.anthracite, color: 'white', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Layers size={18} color="#38bdf8" />
                                <span style={{ fontWeight: '900', fontSize: '12px', letterSpacing: '0.5px' }}>SYNTHÈSE TARIFAIRE</span>
                            </div>
                            <span style={{ color: '#38bdf8', fontSize: '18px', fontWeight: '900' }}>{Math.round(totaux.ttc).toLocaleString()} FCFA (TTC)</span>
                        </div>
                        {isEntrepriseSousTva && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', fontWeight: '700' }}>
                                <span>Total HT : <strong style={{ color: '#fff' }}>{Math.round(totaux.ht).toLocaleString()} F</strong></span>
                                <span>Total TVA : <strong style={{ color: '#38bdf8' }}>{Math.round(totaux.tva).toLocaleString()} F</strong></span>
                            </div>
                        )}
                    </div>

                    {/* ZONE DE SAISIE RAPIDE */}
                    <div style={{ padding: '16px 24px', background: 'white', borderBottom: `1px solid ${COLORS.border}` }}>
                        <div style={{ padding: '12px 16px', background: selectedPrestation ? '#eff6ff' : '#f8fafc', borderRadius: '10px', border: selectedPrestation ? `2px solid ${COLORS.primary}` : `2px dashed ${COLORS.border}`, marginBottom: '12px' }}>
                            <div style={{ fontSize: '10px', fontWeight: '900', color: selectedPrestation ? COLORS.primary : '#94a3b8', marginBottom: '4px' }}>PRESTATION SÉLECTIONNÉE :</div>
                            <div style={{ fontSize: '14px', fontWeight: '900', color: COLORS.anthracite }}>
                                {selectedPrestation ? selectedPrestation.nom : "Aucune prestation sélectionnée dans la liste"}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b', display: 'block', marginBottom: '4px' }}>QUANTITÉ</label>
                                <input 
                                    type="number" 
                                    style={{ width: '100%', padding: '10px', fontSize: '16px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.border}`, borderRadius: '10px', outline: 'none', background: '#fff', boxSizing: 'border-box' }} 
                                    value={quantiteSaisie} 
                                    onChange={(e) => handleQtyChange(e.target.value)} 
                                    min="0.1" 
                                    step="1" 
                                />
                            </div>
                            <div style={{ flex: 1.2 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.success, display: 'block', marginBottom: '4px' }}>MONTANT TTC (FCFA)</label>
                                <input 
                                    type="number" 
                                    style={{ width: '100%', padding: '10px', fontSize: '16px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.success}`, borderRadius: '10px', outline: 'none', background: '#f0fdf4', color: COLORS.success, boxSizing: 'border-box' }} 
                                    value={montantSaisie} 
                                    onChange={(e) => handleAmountChange(e.target.value)} 
                                />
                            </div>
                            <button 
                                onClick={confirmAddLine} 
                                disabled={!selectedPrestation}
                                style={{ height: '46px', padding: '0 18px', background: selectedPrestation ? COLORS.primary : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '900', cursor: selectedPrestation ? 'pointer' : 'not-allowed', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                <Plus size={16} strokeWidth={3}/> AJOUTER
                            </button>
                        </div>
                    </div>

                    {/* LISTE DES ÉLÉMENTS INCLUS */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '900', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase' }}>
                            Éléments inclus dans le tarif ({lignes.length})
                        </div>

                        {lignes.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', height: '100%', padding: '30px', border: `2px dashed ${COLORS.border}`, borderRadius: '12px', background: 'white' }}>
                                <Calculator size={36} strokeWidth={1.5} />
                                <p style={{ fontWeight: '800', fontSize: '12px', marginTop: '8px', color: '#64748b' }}>Aucun élément configuré</p>
                                <p style={{ fontSize: '11px', textAlign: 'center', color: '#94a3b8' }}>Sélectionnez une prestation à gauche.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {lignes.map((ligne) => {
                                    const totalLigne = ligne.prix_unitaire * ligne.quantite;
                                    const hasTva = isEntrepriseSousTva && ligne.soumis_tva && ligne.taux_tva > 0;
                                    const htLigne = hasTva ? totalLigne / (1 + ligne.taux_tva / 100) : totalLigne;
                                    const tvaLigne = hasTva ? totalLigne - htLigne : 0;

                                    return (
                                        <div key={ligne.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'white', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
                                            <div>
                                                <div style={{ fontWeight: '900', fontSize: '13px', color: COLORS.anthracite }}>{ligne.nom}</div>
                                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', marginTop: '2px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <span>Qté : <strong style={{ color: COLORS.primary }}>{ligne.quantite}</strong></span>
                                                    {hasTva && (
                                                        <span style={{ fontSize: '9px', background: '#dcfce7', color: '#15803d', padding: '1px 5px', borderRadius: '4px', fontWeight: '800' }}>
                                                            HT: {Math.round(htLigne).toLocaleString()} F | TVA: {Math.round(tvaLigne).toLocaleString()} F
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{ fontWeight: '900', fontSize: '13px', color: COLORS.success }}>
                                                    {Math.round(totalLigne).toLocaleString()} F
                                                </span>
                                                <button 
                                                    onClick={() => setLignes(lignes.filter(l => l.id !== ligne.id))} 
                                                    style={{ color: '#ef4444', border: 'none', background: '#fee2e2', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'flex' }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* PIED DE PAGE : ACTIONS */}
                    <div style={{ padding: '16px 24px', background: 'white', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: '10px' }}>
                        <button 
                            style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 16px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }} 
                            onClick={() => setLignes([])}
                        >
                            TOUT EFFACER
                        </button>
                        <button 
                            onClick={handleSave} 
                            style={{ flex: 1, background: COLORS.success, color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '12px', boxShadow: '0 4px 12px rgba(5,150,105,0.2)' }}
                        >
                            <Save size={16} /> ENREGISTRER LA CONFIGURATION
                        </button>
                    </div>

                </section>
            </main>
        </div>
    );
};

export default ParametreChambre;